import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

const ConflictWarning = ({ 
  conflictQueue,
  onDontAssign,
  onContinue,
  onSkipCurrent,
}) => {
  const [container, setContainer] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showAssignmentApproval, setShowAssignmentApproval] = useState(false);

  const handleSkip = () => {
    const currentConflict = conflictQueue[currentIndex];
    if (onSkipCurrent) {
      onSkipCurrent(currentConflict);
    }
  };

  useEffect(() => {
    if (currentIndex >= conflictQueue.length && conflictQueue.length > 0) {
      setCurrentIndex(conflictQueue.length - 1);
    }
  }, [conflictQueue.length, currentIndex]);

  useEffect(() => {
    // Find the EnhancedGroupedTrips container
    const findContainer = () => {
      // Try to find the EnhancedGroupedTrips root container
      const elements = document.querySelectorAll('.h-screen.w-screen');
      for (const element of elements) {
        if (element.classList.contains('flex') && element.classList.contains('flex-col')) {
          // Ensure it has relative positioning for absolute children
          if (!element.classList.contains('relative')) {
            element.style.position = 'relative';
          }
          return element;
        }
      }
      // Fallback to body if not found
      return document.body;
    };

    const containerElement = findContainer();
    setContainer(containerElement);
  }, []);

  // Check if we can go back (only if we've navigated forward from the first conflict)
  const canGoBack = () => {
    return currentIndex > 0;
  };

  const handleBack = () => {
    if (canGoBack()) {
      setCurrentIndex(currentIndex - 1);
    } else {
      setCurrentIndex(0);
    }
  };

  const handleNext = () => {
    const conflictQueueLength = conflictQueue.length;
    if (currentIndex < conflictQueueLength - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      setCurrentIndex(conflictQueueLength - 1);
      setShowAssignmentApproval(true);
    }
  };

  const AssignmentApproval = () => {
    return (
      <>
        <div
          style={{ zIndex: 10001 }}
          className="absolute inset-0 bg-black opacity-50"
          onClick={() => onDontAssign(conflictQueue[currentIndex])}
        >
        </div>
        <div style={{ zIndex: 10002 }} className="absolute inset-0 flex items-center justify-center p-4 pointer-events-none">
          <div className="bg-white rounded-xl shadow-lg p-6 max-w-md pointer-events-auto border-2 border-black" style={{ position: 'relative' }}>
            <button 
              className="text-gray-700 hover:text-gray-900 bg-gray-200 hover:bg-gray-300 rounded-full w-6 h-6 flex items-center justify-center" 
              style={{ 
                position: 'absolute',
                top: '0.5rem',
                right: '0.5rem',
                zIndex: 10
              }}
              onClick={() => onDontAssign(conflictQueue[currentIndex])}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <h2 className="text-xl text-purple-500 font-semibold mb-4">
              Timeslot Conflict Warnings
            </h2>
            <p className="mb-6 text-gray-700">
              Warning! Continuing will assign <strong>{conflictQueue.length}</strong> trip{conflictQueue.length > 1 ? 's' : ''} regardless of {conflictQueue.length > 1 ? 'their' : 'its'} conflicts with other trips. If you'd like to unassign more of these conflicting trips, please use the back button.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowAssignmentApproval(false)}
                className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
              >
                Back
              </button>
              <button
                onClick={() => onContinue(conflictQueue[currentIndex])}
                className="px-6 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
              >
                Continue Anyway
              </button>
            </div>
          </div>
        </div>
      </>
    );
  };

  const WarningCarousel = () => {
    const conflict = conflictQueue[currentIndex];
    const isOutboundToInbound = conflict?.conflictType === 'outbound-to-inbound';
    const thresholdText = isOutboundToInbound ? '3 hours' : '1 hour';
    const existingTripLabel = isOutboundToInbound ? 'Existing Outbound Trip' : 'Existing Inbound Trip';
    const currentTripLabel = isOutboundToInbound ? 'Current Inbound Trip' : 'Current Outbound Trip';
    const existingTrip = isOutboundToInbound ? conflict?.outboundTrip : conflict?.inboundTrip;

    return (
      <>
        <div
            style={{ zIndex: 9999 }}
            className="absolute inset-0 bg-black opacity-50"
            onClick={() => onDontAssign(conflictQueue[currentIndex])}
        >
        </div>
        <div style={{ zIndex: 10000 }} className="absolute inset-0 flex items-center justify-center p-4 pointer-events-none">
            <div className="bg-white rounded-xl shadow-lg p-6 max-w-2xl max-h-2xl overflow-y-auto pointer-events-auto" style={{ position: 'relative' }}>
                <button
                    className="text-gray-700 hover:text-gray-900"
                    style={{
                        position: 'absolute',
                        top: '0.5rem',
                        right: '0.5rem',
                        left: 'unset',
                        zIndex: 10
                    }}
                    onClick={()=>onDontAssign(conflictQueue[currentIndex])}
                >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
                <h2 className="text-xl text-purple-500 font-semibold mb-4">
                    { `Time Conflict Warning ${currentIndex + 1}/${conflictQueue.length}` }
                </h2>
                <div className="mb-4 text-sm text-gray-700">
                    <p className="mb-2 max-w-md">
                        The selected time slot for this trip conflicts with another trip scheduled less than {thresholdText} apart.
                    </p>
                    <div className="flex items-center gap-2">
                        <p className="mt-3 text-gray-700">
                            Time Gap:
                        </p>
                        <p className="mt-3 text-orange-600">
                            {conflict?.hoursBetween ? Math.round(conflict.hoursBetween * 60) : 'N/A'} minutes
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                    <p className="mt-3 text-gray-700">
                            Driver:
                        </p>
                        {conflict?.driver && (
                            <p className="mt-3 text-orange-600">
                                {conflict.driver.username} {conflict.driver.usersurname}
                            </p>
                        )}
                    </div>
                    <div className="bg-gray-50 p-3 rounded mt-3">
                        <p className="mb-1">
                            <span className="font-semibold">{existingTripLabel}:</span>
                        </p>
                        {existingTrip && (
                            <div className="border-l-4 border-gray-700 pl-1 rounded-tl rounded-bl">
                                <p className="mb-1 ml-2">
                                    Timeslot & Date: {existingTrip.timeslot} {existingTrip.date}
                                </p>
                                <p className="mb-1 ml-2">
                                    Address: {existingTrip.companyAddress}
                                </p>
                            </div>
                        )}

                        <p className="mb-1 mt-3">
                            <span className="font-semibold">{currentTripLabel}:</span>
                        </p>
                        {conflict?.currentTrip && (
                            <div className="border-l-4 border-gray-700 pl-1 rounded-tl rounded-bl">
                                <p className="mb-1 ml-2">
                                    Timeslot & Date: {conflict.currentTrip.timeslot} {conflict.currentTrip.date}
                                </p>
                                <p className="mb-1 ml-2">
                                    Address: {conflict.currentTrip.companyAddress}
                                </p>
                            </div>
                        )}
                    </div>
                </div>
                <div className="flex justify-between items-center">
                    <button
                        onClick={handleSkip}
                        className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
                    >
                        Don't Assign
                    </button>
                    <div className="flex gap-2">
                        {canGoBack() && (
                            <button
                                onClick={() => handleBack()}
                                className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
                            >
                                Back
                            </button>
                        )}
                        <button
                            onClick={() => handleNext()}
                            className="px-6 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                        >
                            Next
                        </button>
                    </div>
                </div>
            </div>
        </div>
      </>
    );
  };

  if (!container) return null;

  if (showAssignmentApproval) {
    return createPortal(<AssignmentApproval />, container);
  }
  return createPortal(<WarningCarousel />, container);
};

export default ConflictWarning;
